import { supabase } from './supabase'

export type UserMediaHistoryItem = {
  id: string
  user_id: string
  provider: string
  provider_id: string
  canonical_url: string
  title: string | null
  subtitle: string | null
  thumbnail_url: string | null
  reader_content_id: string | null
  last_used_at: string
  created_at: string
  updated_at: string
}

export type MediaHistoryInput = {
  provider: 'youtube'
  providerId: string
  canonicalUrl: string
  title?: string | null
  subtitle?: string | null
  thumbnailUrl?: string | null
  readerContentId?: string | null
}

export function youtubeCanonicalUrl(videoId: string) {
  return `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}`
}

export async function getUserMediaHistory(
  provider?: 'youtube'
): Promise<UserMediaHistoryItem[]> {
  let query = supabase
    .from('user_media_history')
    .select('*')
    .order('last_used_at', { ascending: false })
    .limit(24)

  if (provider) query = query.eq('provider', provider)

  const { data, error } = await query
  if (error) throw error
  return (data ?? []) as UserMediaHistoryItem[]
}

export async function upsertUserMediaHistory(
  userId: string,
  input: MediaHistoryInput
): Promise<UserMediaHistoryItem> {
  const now = new Date().toISOString()
  const { data: existing, error: existingError } = await supabase
    .from('user_media_history')
    .select('*')
    .eq('user_id', userId)
    .eq('provider', input.provider)
    .eq('provider_id', input.providerId)
    .maybeSingle()

  if (existingError) throw existingError

  const existingItem = existing as UserMediaHistoryItem | null
  const { data, error } = await supabase
    .from('user_media_history')
    .upsert(
      {
        user_id: userId,
        provider: input.provider,
        provider_id: input.providerId,
        canonical_url: input.canonicalUrl,
        title:
          input.title === undefined
            ? (existingItem?.title ?? null)
            : input.title,
        subtitle:
          input.subtitle === undefined
            ? (existingItem?.subtitle ?? null)
            : input.subtitle,
        thumbnail_url:
          input.thumbnailUrl === undefined
            ? (existingItem?.thumbnail_url ?? null)
            : input.thumbnailUrl,
        reader_content_id:
          input.readerContentId === undefined
            ? (existingItem?.reader_content_id ?? null)
            : input.readerContentId,
        last_used_at: now,
        updated_at: now
      },
      { onConflict: 'user_id,provider,provider_id' }
    )
    .select()
    .single()

  if (error) throw error
  return data as UserMediaHistoryItem
}
